import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accessibility, X, RotateCcw } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    highlightLinks: false,
    bigCursor: false,
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  // Apply settings to the document
  const applySettings = (newSettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // High contrast
    if (newSettings.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    // Highlight links
    if (newSettings.highlightLinks) {
      document.body.classList.add('highlight-links');
    } else {
      document.body.classList.remove('highlight-links');
    }
    
    // Big cursor
    if (newSettings.bigCursor) {
      document.body.classList.add('big-cursor');
    } else {
      document.body.classList.remove('big-cursor');
    }
  };

  // Save and apply settings
  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  // Font size controls
  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSettings({ ...settings, fontSize: settings.fontSize - 10 });
    }
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSettings({ ...settings, fontSize: settings.fontSize + 10 });
    }
  };

  // Toggle functions
  const toggleHighContrast = () => {
    updateSettings({ ...settings, highContrast: !settings.highContrast });
  };

  const toggleHighlightLinks = () => {
    updateSettings({ ...settings, highlightLinks: !settings.highlightLinks });
  };

  const toggleBigCursor = () => {
    updateSettings({ ...settings, bigCursor: !settings.bigCursor });
  };

  // Reset all settings
  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      highlightLinks: false,
      bigCursor: false,
    };
    updateSettings(defaultSettings);
  };

  return (
    <>
      {/* Accessibility CSS */}
      <style>{`
        .high-contrast {
          filter: contrast(1.25);
        }
        .high-contrast * {
          border-color: #000 !important;
        }
        .highlight-links a {
          background-color: #FFFF00 !important;
          color: #000 !important;
          padding: 2px 4px;
          text-decoration: underline !important;
        }
        .big-cursor,
        .big-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M5.5 2l11 11-4.5 1.5 3 5.5-2 1-3-5.5-4.5 4.5z'/%3E%3C/svg%3E") 0 0, auto !important;
        }
      `}</style>

      {/* Toggle Button - Bottom Left */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 bg-[#1a5ab8] hover:bg-[#14366C] text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Accessibility Options"
        data-testid="accessibility-toggle"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 left-6 z-50 bg-white rounded-lg shadow-2xl w-72 overflow-hidden"
          role="dialog"
          aria-label="Accessibility Settings"
          data-testid="accessibility-panel"
        >
          {/* Header */}
          <div className="bg-[#1a5ab8] text-white px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5" />
              <span className="font-bold">Accessibility</span>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-1 rounded"
              aria-label="Close accessibility panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Font Size */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Font Size
              </label>
              <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
                <button
                  onClick={decreaseFontSize}
                  className="px-4 py-2 bg-white rounded-md shadow-sm hover:bg-gray-50 font-bold text-[#14366C]"
                  aria-label="Decrease font size"
                  data-testid="font-decrease"
                >
                  A-
                </button>
                <span className="font-semibold text-[#14366C]" data-testid="font-size-display">
                  {settings.fontSize}%
                </span>
                <button
                  onClick={increaseFontSize}
                  className="px-4 py-2 bg-white rounded-md shadow-sm hover:bg-gray-50 font-bold text-[#14366C]"
                  aria-label="Increase font size"
                  data-testid="font-increase"
                >
                  A+
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">High Contrast</span>
              <button
                onClick={toggleHighContrast}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.highContrast ? 'bg-[#1a5ab8]' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.highContrast}
                data-testid="high-contrast-toggle"
              >
                <span 
                  className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    settings.highContrast ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Highlight Links */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Highlight Links</span>
              <button
                onClick={toggleHighlightLinks}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.highlightLinks ? 'bg-[#1a5ab8]' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.highlightLinks}
                data-testid="highlight-links-toggle"
              >
                <span 
                  className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    settings.highlightLinks ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Big Cursor */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Big Cursor</span>
              <button
                onClick={toggleBigCursor}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.bigCursor ? 'bg-[#1a5ab8]' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={settings.bigCursor}
                data-testid="big-cursor-toggle"
              >
                <span 
                  className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    settings.bigCursor ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Reset Button */}
            <button
              onClick={resetSettings}
              className="w-full flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-semibold transition-colors"
              data-testid="reset-settings"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Settings
            </button>

            {/* Accessibility Statement Link */}
            <div className="pt-2 border-t border-gray-200">
              <Link
                to="/website-accessibility"
                className="block text-center text-sm text-[#1a5ab8] hover:underline"
                onClick={() => setIsOpen(false)}
                data-testid="accessibility-statement-link"
              >
                Accessibility Statement
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
